import { SuprSendProvider } from "@suprsend/react";
import { createFileRoute, Outlet } from "@tanstack/react-router";
import { useEffect } from "react";

import { FirstDraftExpiredBanner } from "@/components/first-draft-expired-banner";
import { Navbar } from "@/components/navbar";
import { Sidebar, SidebarProvider, useSidebar } from "@/components/sidebar";
import { TrialRibbon } from "@/components/trial-ribbon";
import { env } from "@/config/env";
import ForceUpdatePasswordModal from "@/features/users/forceUpdatePassword";
import { usePosthogIdentify } from "@/hooks/posthog/usePosthogIdentify";
import { useIsWhitelabelDashboard } from "@/hooks/useIsWhitelabelOwner";
import { useUser } from "@/hooks/useUser.tsx";
import { useWhitelabel } from "@/hooks/useWhitelabel";
import { useWhitelabelConfig } from "@/hooks/useWhitelabelConfig";
import { cn } from "@/utils/cn";

function AppShellContent() {
  const { isMobile, state } = useSidebar();
  // The whitelabel owner navigates the dashboard through cards, not the sidebar,
  // so the sidebar is stripped there only. Inside an account the chrome returns.
  const isWhitelabelDashboard = useIsWhitelabelDashboard();

  return (
    <div className="fixed inset-0 flex flex-col touch-pan-y bg-bg-tertiary">
      {!isWhitelabelDashboard && <Sidebar />}
      <Navbar />
      <TrialRibbon />
      <main
        className={cn([
          "flex-1 min-h-0 bg-bg-tertiary overflow-x-hidden overflow-y-auto overscroll-y-contain touch-pan-y",
          "transition-[margin] duration-300 ease-in-out",
          !isMobile &&
            (isWhitelabelDashboard
              ? "ml-0"
              : state === "collapsed"
                ? "ml-[48px]"
                : "ml-[170px]"),
        ])}
      >
        <FirstDraftExpiredBanner />
        <Outlet />
      </main>
    </div>
  );
}

export function LayoutComponent() {
  usePosthogIdentify();

  const { data: user } = useUser();

  const { setAuthenticatedConfig } = useWhitelabel();
  const { data: whitelabelData } = useWhitelabelConfig();

  useEffect(() => {
    const html = document.documentElement;
    const body = document.body;
    const prev = {
      bodyHeight: body.style.height,
      bodyOverflow: body.style.overflow,
      htmlHeight: html.style.height,
      htmlOverflow: html.style.overflow,
    };

    html.style.overflow = "hidden";
    html.style.height = "100%";
    body.style.overflow = "hidden";
    body.style.height = "100%";

    return () => {
      html.style.overflow = prev.htmlOverflow;
      html.style.height = prev.htmlHeight;
      body.style.overflow = prev.bodyOverflow;
      body.style.height = prev.bodyHeight;
    };
  }, []);

  useEffect(() => {
    if (whitelabelData) {
      setAuthenticatedConfig(whitelabelData);
    }
  }, [whitelabelData, setAuthenticatedConfig]);

  return (
    <SuprSendProvider
      distinctId={user?.notificationDistinctId}
      publicApiKey={env.VITE_SUPRSEND_PUBLIC_KEY}
    >
      <SidebarProvider>
        <ForceUpdatePasswordModal
          mustChangePassword={user?.mustChangePassword}
        />
        <AppShellContent />
      </SidebarProvider>
    </SuprSendProvider>
  );
}

export const Route = createFileRoute("/_auth/_app-shell")({
  component: LayoutComponent,
});
